"use client";

import { LoadingSpinner } from "@/app/[lang]/_components/LoadingSpinner";
import { createT } from "@/app/[lang]/_utils/create-t";
import type { Lang } from "@/langs";
import type { AppRouter } from "@/server/api/root";
import { api } from "@/trpc/react";
import type { PostGridData } from "@/validators/post";
import { Button } from "@borderless/ui/components/button";
import { cn } from "@borderless/ui/lib/utils";
import type { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, type FC } from "react";
import { Search } from "../Search";
import { StoryInList } from "../StoryInList";
import { StoryPlaylistSwitcher } from "../StoryPlaylistSwitcher";
import dictionary from "./t.json";

const t = createT(dictionary);

type Props = {
  initialData: inferRouterOutputs<AppRouter>["post"]["findRelevant"];
  promotedPinnedPost?: PostGridData | null;
  lang: Lang;
};

export const StoriesList: FC<Props> = (props) => {
  return (
    <Suspense>
      <StoriesListContent {...props} />
    </Suspense>
  );
};

const StoriesListContent: FC<Props> = (props) => {
  const { data, fetchNextPage, hasNextPage, isFetching } =
    api.post.findRelevant.useInfiniteQuery(
      {
        lang: props.lang,
        limit: 10,
        types: ["Bachelor", "Master", "PhD", "HighSchool", "General"],
      },
      {
        initialData: {
          pages: [props.initialData],
          pageParams: [undefined],
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        refetchOnMount: false, // posts don't change often
      },
    );
  const ref = useRef<HTMLButtonElement>(null);
  const searchParams = useSearchParams();
  const searchTerm = searchParams.get("q");
  useEffect(() => {
    const rootMarginPixels = window.innerHeight * 0.5;
    const rootMarginValue = `${rootMarginPixels}px`;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage) {
          void fetchNextPage();
        }
      },
      {
        threshold: 0,
        root: null,
        // null means the viewport
        rootMargin: `0px 0px ${rootMarginValue} 0px`, // Apply the calculated margin to the bottom
      },
    );
    const el = ref.current;
    if (el) {
      observer.observe(el);
    }
    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [fetchNextPage, hasNextPage, searchTerm]);
  return (
    <>
      <div className="mx-auto pb-8">
        <StoryPlaylistSwitcher lang={props.lang} />
      </div>
      <Search lang={props.lang} />
      {(searchTerm === null || searchTerm.length === 0) && (
        <>
          <ul className="mx-auto max-w-3xl space-y-6">
            {props.promotedPinnedPost && (
              <li key={"promo-post"}>
                <StoryInList
                  isPromoted
                  post={props.promotedPinnedPost}
                  lang={props.lang}
                />
              </li>
            )}
            {data.pages.slice(0, 1).map((page) =>
              page.items.slice(0, 2).map((post) => (
                <li key={post.slug}>
                  <StoryInList post={post} lang={props.lang} />
                </li>
              )),
            )}
            {/* <li key={"org-in-list"} className="px-8">
              <PartnerUniversityPromo
                lang={props.lang}
                variant="main"
                className="my-0 md:my-0"
              />
            </li> */}
            {data.pages.map((page, pageIndex) => {
              if (pageIndex === 0) {
                return page.items.slice(2).map((post) => (
                  <li key={post.slug}>
                    <StoryInList post={post} lang={props.lang} />
                  </li>
                ));
              }
              return page.items.map((post) => (
                <li key={post.slug}>
                  <StoryInList post={post} lang={props.lang} />
                </li>
              ));
            })}
          </ul>
          <div className="h-8" />
          <div className="mx-auto w-fit">
            {isFetching && <LoadingSpinner />}
            {hasNextPage ? (
              <Button
                ref={ref}
                variant="outline"
                onClick={() => fetchNextPage()}
                className={cn(isFetching && "hidden")}
              >
                {t("Load more", props.lang)}
              </Button>
            ) : (
              <div className="text-lg">
                <span>{t("🎉You've reached the end!", props.lang)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
